import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { publicApi } from "../../admin/api";
import { needsInvert, resolveImage } from "../../lib/data";
import { useSettings } from "../../lib/useSettings";

/**
 * Striscia scorrevole in alto alla home.
 * Priorità: se ci sono `marquee_logos` gestiti da CMS → usali.
 * Altrimenti fallback a loghi degli hotel (comportamento legacy).
 */
export default function Marquee() {
  const [items, setItems] = useState([]);
  const settings = useSettings();
  const speedSeconds = Number(settings?.marquee_speed_seconds) || 40;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await publicApi.get("/marquee-logos");
        if (cancelled) return;
        if (r.data?.length) {
          setItems(
            r.data.map((it) => ({
              key: it.id,
              logo: it.image,
              name: it.name,
              caption: it.caption,
              url: it.url,
            })),
          );
          return;
        }
        // fallback: hotels
        const h = await publicApi.get("/hotels");
        if (cancelled) return;
        setItems(
          h.data.map((ht) => ({
            key: ht.id,
            logo: ht.logo,
            name: ht.name,
            caption: `${"★".repeat(ht.stars)} · ${ht.area}`,
            url: `/hotel/${ht.slug}`,
          })),
        );
      } catch {
        setItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!items.length) return null;
  const doubled = [...items, ...items];

  return (
    <section
      data-testid="marquee-section"
      className="relative py-12 md:py-14 border-y border-white/5 overflow-hidden bg-[#0B0B0F]"
    >
      <div
        className="flex gap-10 md:gap-16 animate-marquee whitespace-nowrap items-center"
        style={{ animationDuration: `${speedSeconds}s` }}
      >
        {doubled.map((it, i) => {
          const content = (
            <>
              {it.logo ? (
                <img
                  src={resolveImage(it.logo)}
                  alt={it.name}
                  className={`h-14 md:h-16 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity ${
                    needsInvert(it.logo) ? "brightness-0 invert" : ""
                  }`}
                  style={{ maxWidth: 220 }}
                />
              ) : (
                <span className="font-display text-3xl md:text-4xl text-white/90 italic">
                  {it.name}
                </span>
              )}
              {it.caption && (
                <span className="font-mono text-[10px] tracking-[0.2em] text-[#C48F56] uppercase">
                  {it.caption}
                </span>
              )}
              <span className="h-1 w-1 rounded-full bg-white/20" />
            </>
          );

          const className = "flex items-center gap-4 md:gap-5 shrink-0";
          const key = `${it.key}-${i}`;

          if (it.url) {
            const isExternal = /^https?:\/\//i.test(it.url);
            if (isExternal) {
              return (
                <a
                  key={key}
                  href={it.url}
                  target="_blank"
                  rel="noreferrer"
                  className={className}
                  data-testid={`marquee-logo-${it.key}`}
                >
                  {content}
                </a>
              );
            }
            // Internal link: use React Router <Link> so hash scrolling + SPA nav work
            return (
              <Link
                key={key}
                to={it.url}
                className={className}
                data-testid={`marquee-logo-${it.key}`}
              >
                {content}
              </Link>
            );
          }
          return (
            <div key={key} className={className}>
              {content}
            </div>
          );
        })}
      </div>
    </section>
  );
}
